import React, { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Button from '../../../components/Button';
import { toast } from 'react-toastify';
import ReCAPTCHA from 'react-google-recaptcha';

interface ContactFormData {
  name: string;
  email: string;
  mobile: string;
  message: string;
}

const schema = yup.object({
  name: yup
    .string()
    .min(2, 'Name must be at least 2 characters')
    .max(50, 'Name must be at most 50 characters')
    .required('Name is required'),
  email: yup.string().email('Invalid email').required('Email is required'),
  mobile: yup
    .string()
    .matches(/^\d{10}$/, 'Mobile number must be 10 digits')
    .required('Mobile number is required'),
  message: yup
    .string()
    .min(10, 'Message must be at least 10 characters')
    .max(500, 'Message must be at most 500 characters')
    .required('Message is required'),
});

function ContactForm() {
  const [loading, setLoading] = useState(false);
  // ReCAPTCHA site key
  const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY as string;
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [captchaError, setCaptchaError] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContactFormData>({
    resolver: yupResolver(schema),
  });

  // Function to handle form submission
  const onSubmit = async (data: ContactFormData) => {
    setLoading(true); // Show loader
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/php/email.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      console.log(response, ':response Message');
      const result = await response.json();
      if (result.status === 'success') {
        toast.success(result.message);
        setLoading(false); // Hide loader
        reset();
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'An unexpected error occurred');
    } finally {
      setLoading(false); // Hide loader
    }
  };
  return (
    <form className="contact-form" onSubmit={handleSubmit(onSubmit)} autoComplete="off">
      <input
        type="text"
        placeholder="Your Name"
        {...register('name')}
        minLength={2}
        maxLength={50}
        className={errors.name ? 'input-error' : ''}
      />
      {errors.name && <p className="error">{errors.name.message}</p>}

      {/* Email Input */}
      <input
        type="email"
        placeholder="Your Email"
        {...register('email')}
        className={errors.email ? 'input-error' : ''}
      />
      {errors.email && <p className="error">{errors.email.message}</p>}
      <input
        type="number"
        placeholder="Your Mobile No"
        {...register('mobile')}
        className={errors.mobile ? 'input-error' : ''}
      />
      {errors.mobile && <p className="error">{errors.mobile.message}</p>}

      {/* Message Textarea */}
      <textarea
        placeholder="Your Message"
        rows={5}
        {...register('message')}
        minLength={10}
        maxLength={500}
        className={errors.message ? 'input-error' : ''}
      />
      {errors.message && <p className="error">{errors.message.message}</p>}
      <div>
        <ReCAPTCHA sitekey={SITE_KEY} ref={recaptchaRef} onChange={() => setCaptchaError(false)} />
        {captchaError && <p>Please verify that you are not a robot.</p>}
      </div>
      {/* Submit Button */}
      <div className="submit-btn">
        <div className="button-wrapper">
          {loading && <div className="loader"></div>}
          <Button label={'Submit'} disabled={loading} className={loading ? 'loading-button' : ''} />
        </div>
      </div>
    </form>
  );
}

export default ContactForm;
